import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { getGymChainAccessCategories } from '@gym-graphql/queries';
import { GymChainAccessCategoryType } from '@gym-particles/types/models';
import { GymChainAccessCategories } from '@gym-src/API';
import { removeSiteAccessCategory } from '../../graphql/mutations';
import dayjs from 'dayjs';

const initialState: {
  isAccessCategoriesLoading: boolean;
  items: Array<{ siteId: number; items: GymChainAccessCategoryType[]; totalRecords: number }>;
} = {
  isAccessCategoriesLoading: false,
  items: []
};

type paramType = {
  siteId: number;
};

export const fetchGymChainAccessCategory = createAsyncThunk(
  '/gym/gymChainAccessCategory',
  async (params: paramType) => {
    console.log(params);
    const response = await (API.graphql({
      query: getGymChainAccessCategories,
      variables: {
        siteId: params.siteId
      }
    }) as Promise<{
      data: {
        getGymChainAccessCategories: {
          items: GymChainAccessCategories[];
          totalRecords: { totalRecords: number };
        };
      };
    }>);
    console.log(response);
    return response;
  }
);

export const deleteGymChainAccessCategory = createAsyncThunk(
  '/gym/deleteGymChainAccessCategory',
  async (deleteAccessCategoryIds: number[]) => {
    const response = await API.graphql({
      query: removeSiteAccessCategory,
      variables: {
        ids: deleteAccessCategoryIds
      }
    });
    console.log(response);
    return response;
  }
);

export const gymChainAccessCategoriesSlice = createSlice({
  name: 'GymChainAccessCategory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchGymChainAccessCategory.fulfilled, (state, action) => {
      state.isAccessCategoriesLoading = false;
      if (!action.payload.data.getGymChainAccessCategories.items) {
        return;
      }

      const items = action.payload.data.getGymChainAccessCategories.items.map(
        (e: GymChainAccessCategories): GymChainAccessCategoryType => {
          return {
            id: e.id || 0,
            siteId: e.siteId || 0,
            name: e.name || '',
            gender: e.gender || '',
            age: e.age || 0,
            schedulesOnly: e.schedulesOnly ? 'true' : 'false',
            status: e.status || '',
            siteLocationAccessCategoryId: e.siteLocationAccessCategoryId || 0,
            siteLocationId: e.siteLocationId || 0,
            siteLocationMembershipId: e.siteLocationMembershipId || 0,
            accessCategoryId: e.accessCategoryId || 0,
            locationName: e.locationName || '',
            arxAccessCategoryId: e.arxAccessCategoryId || '',
            membershipImportedId: e.importedId || '',
            membershipName: e.membershipName || '',
            program: e.program || '',
            scheduleType: e.scheduleType || '',
            createdDate: e.createdDate ? dayjs(e.createdDate || '').format('DD MMM YYYY') : '',
            createdBy: e.createdBy || 0,
            lastModifiedBy: e.lastModifiedBy || 0,
            lastModifiedDate: e.lastModifiedDate || ''
          };
        }
      );
      console.log({ items });

      const totalRecords =
        action.payload.data.getGymChainAccessCategories.totalRecords.totalRecords;

      const fetchGymChainAccessCategoryInfo = {
        totalRecords: totalRecords,
        items: items,
        siteId: action.meta.arg.siteId
      };

      if (state.items.length === 0) {
        state.items.push(fetchGymChainAccessCategoryInfo);
      } else {
        const update = state.items.find(
          (gymChainAccessCategoryInfo) =>
            gymChainAccessCategoryInfo.siteId === fetchGymChainAccessCategoryInfo.siteId
        );
        if (update) {
          const newState = state.items.map((accessCategoryInfo) => {
            if (accessCategoryInfo.siteId === fetchGymChainAccessCategoryInfo.siteId) {
              return fetchGymChainAccessCategoryInfo;
            } else {
              return accessCategoryInfo;
            }
          });
          state.items = newState;
        } else {
          state.items.push(fetchGymChainAccessCategoryInfo);
        }
      }
    });

    builder.addCase(fetchGymChainAccessCategory.pending, (state, action) => {
      state.isAccessCategoriesLoading = true;
      return state;
    });

    builder.addCase(fetchGymChainAccessCategory.rejected, (state, action) => {
      state.isAccessCategoriesLoading = false;
      console.log('Fetch access categories error');
      return state;
    });

    builder.addCase(deleteGymChainAccessCategory.fulfilled, (state, action) => {
      state.isAccessCategoriesLoading = false;
      const accessCategoryId = action.meta.arg;
      const filtered = state.items.map((accessCategoryObj) => {
        const filteredAccessCategories = accessCategoryObj.items.filter(
          (accessCategory) => !accessCategoryId.includes(accessCategory.id)
        );
        accessCategoryObj.items = filteredAccessCategories;
        accessCategoryObj.totalRecords = accessCategoryObj.totalRecords - 1;
        return accessCategoryObj;
      });
      state.items = filtered;
    });

    builder.addCase(deleteGymChainAccessCategory.pending, (state, action) => {
      state.isAccessCategoriesLoading = true;
      return state;
    });

    builder.addCase(deleteGymChainAccessCategory.rejected, (state, action) => {
      state.isAccessCategoriesLoading = false;
      console.error('deleteAccessCategory', action);
      return state;
    });
  }
});

export default gymChainAccessCategoriesSlice.reducer;
